import {
  chunk,
  compact,
  every,
  first,
  flatten,
  flattenDeep,
  forOwn,
  isArray,
  isBoolean,
  isPlainObject,
  isString,
  last
} from "lodash-es";
import { LoadOptions } from "devextreme/data/load_options";
import { FilterDescriptor } from "devextreme/data";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { sntTranslate } from "src/app/support/translate";
import CustomStore from "devextreme/data/custom_store";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { map } from "rxjs/operators";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import DataSource from "devextreme/data/data_source";
import { Pet } from "src/app/backend/models/pet/pet";
import { ApplicationRef, createComponent } from "@angular/core";
import { BlockPatientComponent } from "src/app/modules/bootstrap/pms-ui/components/ui/block-patient/block-patient.component";
import DevExpress from "devextreme";
import DxForm from "devextreme/ui/form";
import { DxFormComponent } from "devextreme-angular";
import { runAsyncOperation } from "src/app/support/async-operation";

export async function isDxFormValid(dxForm: DxForm): Promise<boolean> {
  try {
    let result = dxForm.validate();
    if (result.complete) {
      result = await result.complete!;
    }

    return !!result.isValid;
  } catch (ex) {
    return false;
  }
}

export function getDataFieldByFormName(formName: string): string {
  return last(formName.split(".")) || "";
}

export function dxGetFilters<T = Record<string, string>>(
  loadOptions: LoadOptions
): T {
  const filters: Record<string, string> = {};

  chunk(
    flattenDeep<string>(loadOptions.filter || []).filter(
      (x) => x !== "=" && x !== "or" && x !== "and"
    ),
    2
  ).forEach(([field, value]) => {
    if (filters[field]) {
      filters[field] = `${filters[field]},${value}`;
    } else {
      filters[field] = value;
    }
  });

  return <T>filters;
}

export function getListParamByLoadOptions(
  loadOptions: LoadOptions,
  ignoreDefault = false
): {
  page: number;
  pageSize: number;
  queryString: string;
  filterValues: Record<string, string>;
} {
  const skip = loadOptions.skip || (ignoreDefault ? undefined : 0);
  const pageSize = loadOptions.take || (ignoreDefault ? undefined : 25);
  const page =
    pageSize === undefined && ignoreDefault
      ? undefined
      : Math.max(Math.floor(skip / pageSize) + 1, 1);
  const queryString = getQueryStringFromFilterLoadOptions(loadOptions);

  const processTuple = (filterTuple: string[]) => {
    if (isArray(filterTuple[0])) {
      processTuple(filterTuple[0]);
      return;
    }

    const filterName = filterTuple[0];
    const filterValue = filterTuple[2];

    filterValues[filterName] = filterValues[filterName]
      ? `${filterValues[filterName]},${filterValue}`
      : filterValue;
  };

  const filters = loadOptions.filter || [];

  const filterValues: Record<string, string> = {};

  if (isArray(filters[0])) {
    filters
      .filter((x: FilterDescriptor[] | string) => !isString(x))
      .forEach((filterTuple: any) => {
        if (isArray(filterTuple[0])) {
          filterTuple.forEach((x: string[]) => {
            processTuple(x);
          });
        } else {
          processTuple(filterTuple);
        }
      });
  }

  if (
    every(filters, (x) => isString(x) || isBoolean(x)) &&
    filters.length > 0
  ) {
    filterValues[filters[0]] = filters[2];
  }

  return {
    page,
    pageSize,
    queryString,
    filterValues
  };
}

export function getSelectParamByLoadOptions(loadOptions: LoadOptions): {
  page: number;
  pageSize: number;
  queryString: string;
} {
  const skip = loadOptions.skip || 0;
  const pageSize = loadOptions.take || 25;
  const page = Math.max(Math.floor(skip / pageSize) + 1, 1);
  const queryString = loadOptions.searchValue || undefined;

  return {
    page,
    pageSize,
    queryString
  };
}

export function getQueryStringFromFilterLoadOptions(
  loadOptions: LoadOptions
): string {
  if (!loadOptions.filter || !loadOptions.filter.length) {
    return "";
  }

  const dxFilter: string[] | string[][] = loadOptions.filter;

  if (!isArray(first(<string[]>dxFilter))) {
    return `${dxFilter[0]}=${dxFilter[2]}`;
  }

  return (<string[][]>dxFilter)
    .filter((filterColumnOrOperator) => isArray(filterColumnOrOperator))
    .map((filterColumn) => {
      return `${filterColumn[0]}=${filterColumn[2]}`;
    })
    .join(",");
}

type DevxFilterExpression = any[];
type DevxBooleanOperator = "and" | "or";
type DevxFilterCondition = { left: string; op: "!" | "="; right: string };
export type DevxFilterParseResult = {
  booleanOperator: DevxBooleanOperator;
  conditions: Array<DevxFilterCondition>;
};

function isLeaf(value: any) {
  return (
    isArray(value) &&
    value.length === 3 &&
    isString(value[0]) &&
    isString(value[1]) &&
    !isArray(value[2])
  );
}

function isNot(value: any): boolean {
  return isArray(value) && value[0] === "!" && isArray(value[1]);
}

function simplifySingle(
  array: DevxFilterExpression,
  boolean: DevxBooleanOperator
): null | DevxFilterParseResult {
  const filtered = array.filter((x) => isArray(x));

  if (filtered.length === 0) {
    return null;
  }

  if (filtered.length === 1) {
    return devxParseFilter(array[0]);
  }

  return {
    booleanOperator: boolean,
    conditions: flatten(
      compact(filtered.map((x) => devxParseFilter(x).conditions))
    )
  };
}

export function devxParseFilter(
  input: DevxFilterExpression
): null | DevxFilterParseResult {
  if (!isArray(input)) {
    return input;
  }

  const isNotValue = isNot(input);
  if (isNotValue || isLeaf(input)) {
    return {
      booleanOperator: "and",
      conditions: [
        {
          left: isNotValue ? input[1][0] : input[0],
          op: isNotValue ? "!" : input[1],
          right: isNotValue ? input[1][2] : input[2]
        }
      ]
    };
  }

  const orPosition = input.indexOf("or");
  const andPosition = input.indexOf("and");

  const mixed = orPosition > -1 && andPosition > -1;

  if (mixed) {
    // when we face mixed operators we split on the OR
    // since AND have higher precedence
    return simplifySingle(
      [input.slice(0, orPosition), input.slice(orPosition + 1)],
      "or"
    );
  }

  return simplifySingle(input, orPosition > -1 ? "or" : "and");
}

export function getDxCustomLabelFormItem(
  label: string,
  icon: string | undefined,
  required?: boolean,
  tooltipText?: string,
  helpText?: string,
  options?: {
    requiredFnProviders: (requiredFn: (required: boolean) => void) => void;
  }
) {
  return {
    template: (data: any, element: HTMLElement) => {
      element.innerHTML = buildDxCustomLabelFormItemHTML({
        icon,
        label,
        required,
        tooltipText,
        helpText
      });

      if (options?.requiredFnProviders) {
        options.requiredFnProviders((required: boolean) => {
          element.innerHTML = buildDxCustomLabelFormItemHTML({
            icon,
            label,
            required,
            tooltipText,
            helpText
          });
        });
      }
    }
  };
}

function buildDxCustomLabelFormItemHTML({
  icon,
  label,
  required,
  tooltipText,
  helpText
}: {
  icon: string | undefined;
  label: string;
  required?: boolean;
  tooltipText?: string;
  helpText?: string;
}): string {
  return `
        <div class="flex flex-col">
          <div class="flex flex-row items-center -mb-2">
            ${
              icon
                ? `<div class="flex items-center justify-center w-6 h-6">
                            <i class="${icon} text-slate-400"></i>
                        </div>`
                : ""
            }
            
            <div class="flex flex-col">
              <div class="flex flex-row">
                <span class="text-xs text-slate-400">${label}</span>
                ${required ? `<sup class="text-xs text-red-500">*</sup>` : ""}
                ${
                  tooltipText
                    ? `<div class="ml-auto"><sl-tooltip content="${tooltipText}"><i class="fa-light fa-circle-info text-slate-400"></i></sl-tooltip></div>`
                    : ""
                }
              </div>

              ${
                helpText
                  ? `<div><span class="text-xs text-slate-400 italic">${helpText}</span></div>`
                  : ``
              }
            </div>            
          </div>
          </div>`;
}

export function getDxFormItemPatientId(
  customerIdFn: () => string | undefined,
  providers: {
    customerBackend: CustomerBackend;
    patientBackend: PetBackend;
    applicationRef: ApplicationRef;
  },
  options?: {
    readOnly?: boolean | undefined;
  },
  label?: {
    text?: string;
    visible?: boolean;
  }
): DevExpress.ui.dxForm.Item {
  return {
    dataField: "patientId",
    label: {
      ...{
        text: sntTranslate(marker("Paziente")),
        location: "top",
        showColon: false
      },
      ...(label || {})
    },
    editorType: "dxSelectBox",
    editorOptions: {
      readOnly: options?.readOnly || false,
      dataSource: new DataSource({
        store: new CustomStore({
          byKey: async (key: string) => {
            return await providers.patientBackend
              .load({ id: key })
              .pipe(
                map((pet) => ({
                  ...pet,
                  value: pet.id,
                  display: pet.name
                }))
              )
              .toPromise();
          },
          load: async (options) => {
            const { page, pageSize, queryString } =
              getSelectParamByLoadOptions(options);

            const customerId = customerIdFn();

            if (customerId) {
              return await providers.customerBackend
                .load(customerId)
                .pipe(map((customer) => customer.pets || []))
                .toPromise();
            }

            return await providers.patientBackend
              .list({ page, pageSize })
              .toPromise();
          }
        })
      }),
      valueExpr: "id",
      displayExpr: function (patient: Pet | null) {
        return patient?.name;
      },
      searchEnabled: false,
      itemTemplate: (patient: Pet, index: number, element: HTMLElement) => {
        const componentRef = createComponent(BlockPatientComponent, {
          environmentInjector: providers.applicationRef.injector,
          hostElement: element
        });

        componentRef.instance.patient = patient;
        componentRef.changeDetectorRef.detach();
        componentRef.changeDetectorRef.detectChanges();
      }
    }
  };
}

export function getDxSortParams<T extends string>(
  loadOptions: LoadOptions
): Partial<Record<T, "desc" | "asc">> {
  if (isArray(loadOptions.sort)) {
    return loadOptions.sort.reduce(
      (accumulator, current: any) => {
        accumulator[<T>current.selector] = current.desc ? "desc" : "asc";
        return accumulator;
      },
      <Record<T, "desc" | "asc">>{}
    );
  }

  return {};
}

export async function dxFormRunAsyncOperation<T>(
  dxForm: DxFormComponent,
  promiseFn: () => Promise<T>
): Promise<T> {
  return await runAsyncOperation(
    dxForm.instance.element().parentElement!,
    promiseFn
  );
}

export function dxPlainObjectToTreeDataSource(
  plainObject: Record<string, any>,
  parentDataSource?: Array<any>
): Array<any> {
  if (!parentDataSource) {
    parentDataSource = [];
  }

  switch (true) {
    case isString(plainObject): {
      parentDataSource.push({ label: plainObject });
      break;
    }
    case isArray(plainObject): {
      plainObject.forEach((item) => {
        dxPlainObjectToTreeDataSource(item, parentDataSource);
      });

      break;
    }
    case isPlainObject(plainObject): {
      forOwn(plainObject, (value, key) => {
        const dataSourceItem: { label: string; items: string[] } = {
          label: key,
          items: []
        };

        dxPlainObjectToTreeDataSource(value, dataSourceItem.items);

        parentDataSource.push(dataSourceItem);
      });
      break;
    }
  }

  return parentDataSource;
}
